/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useEffect, useRef, useState } from 'react';
import { TextField, TextFieldClasses, SxProps } from '@mui/material';
import { loadGoogleScript } from 'common/utils/load-google-script';
import { TextFieldProps } from '@mui/material/TextField';
import { getIn, useFormikContext } from 'formik';
import { SignUpFormValues } from 'modules/sign-up/sign-up';

export interface LatLong {
  lat: number;
  lng: number;
}

interface Props {
  name?: string;
  onBlur?: () => void;
  classes?: Partial<TextFieldClasses>;
  disabled?: boolean;
  onFocus?: () => void;
  id?: string;
  placeholder?: string;
  sx?: SxProps;
  size?: TextFieldProps['size'];
  fullWidth?: TextFieldProps['fullWidth'];
}

function AddressInput({
  name,
  onBlur,
  classes,
  disabled,
  onFocus,
  id,
  placeholder,
  sx,
  size,
  fullWidth,
}: Props): ReactElement {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<SignUpFormValues>();
  const [scriptLoaded, setScriptLoaded] = useState<boolean>(false);

  const autocomplete = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentValue = getIn(values, name);
  const fieldError = getIn(errors, name);
  const isFieldTouched = getIn(touched, name);

  const handleInputFocus = () => {
    if (!isFieldTouched) {
      setFieldTouched(name, true);
    }
    if (onFocus) {
      onFocus();
    }
  };

  useEffect(() => {
    loadGoogleScript()
      .then(() => setScriptLoaded(true))
      // eslint-disable-next-line no-console
      .catch(err => console.error('Error loading Google Maps script:', err));
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (scriptLoaded && inputRef.current) {
      autocomplete.current = new (window as any).google.maps.places.Autocomplete(inputRef.current, {
        fields: ['formatted_address', 'geometry', 'name'],
      });

      const handlePlaceChange = () => {
        const place = autocomplete.current.getPlace();
        if (place && place.formatted_address) {
          setFieldValue(name, place.formatted_address);
        }
      };

      const listener = autocomplete.current.addListener('place_changed', handlePlaceChange);

      return () => {
        if ((window as any).google) {
          (window as any).google.maps.event.removeListener(listener);
        }
      };
    }
  }, [scriptLoaded, name, setFieldValue]);

  return (
    <TextField
      name={name}
      inputRef={inputRef}
      size={size}
      onBlur={onBlur || (() => setFieldTouched(name, true))}
      onFocus={handleInputFocus}
      placeholder={placeholder ?? ''}
      classes={classes}
      error={isFieldTouched && Boolean(fieldError)}
      helperText={isFieldTouched && fieldError}
      disabled={disabled}
      id={id}
      sx={sx}
      fullWidth={fullWidth}
      value={currentValue ?? ''}
      onChange={e => setFieldValue(name, e.target.value)}
      label="Address"
    />
  );
}

export default AddressInput;
